import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { Fragment, useState } from 'react';
import { Link } from 'react-router';
import { Card } from '../../components/ui/Card.js';
import { Spinner } from '../../components/ui/Spinner.js';
import { api } from '../../services/api.js';
import { PENALTY_TYPE_LABEL, type PenaltyType } from '../penalties/penalties.js';
import { monthName } from './dashboardSeries.js';
import type { HostMoney as HostMoneyData } from './types.js';

/**
 * 424 (#1542, mock-up approved 2026-10-27): a host's own money, between Today and This month. Every figure is the
 * Sales Statement's for the same whole month (`GET /dashboard/money`); this screen formats, never computes, except
 * the shares of the bars. Its read is its own (FR-011): failing here never blanks Today or This month.
 */
const money = (minor: number) =>
  `RM ${(Math.abs(minor) / 100).toLocaleString('en-MY', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const signed = (minor: number) => (minor < 0 ? `−${money(minor)}` : money(minor));

const MYT = 'Asia/Kuala_Lumpur';
const dayOf = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', { timeZone: MYT, day: 'numeric', month: 'short' });
const paidDay = (iso: string) =>
  new Date(iso).toLocaleDateString('en-GB', {
    timeZone: MYT,
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
/** `2026-10-24` → `24 Oct` (a Malaysia day as stored; no zone involved). */
const penaltyDay = (day: string) => {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-GB', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'short',
  });
};
const shiftMonth = (month: string, by: number) => {
  const [y, m] = month.split('-').map(Number) as [number, number];
  const d = new Date(Date.UTC(y, m - 1 + by, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
};
const MISSING: Record<'monthly_cost' | 'commission_rate', string> = {
  monthly_cost: 'monthly cost',
  commission_rate: 'commission rate',
};

const PILL =
  'inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold whitespace-nowrap';
const LABEL = 'text-[11px] font-semibold tracking-wider text-steel uppercase';

function Line({ label, value }: { label: React.ReactNode; value: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-hairline-soft py-2 text-sm tabular-nums">
      <span className="text-slate">{label}</span>
      <span className="whitespace-nowrap text-ink">{value}</span>
    </div>
  );
}

function Payout({ data }: { data: HostMoneyData }) {
  const t = data.totals;
  const paid = data.payment?.paid === true;
  return (
    <Card data-testid="host-payout" className="min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-x-3 gap-y-1">
        <div className="min-w-0">
          <div className={LABEL}>Payout from CARii</div>
          <div
            data-testid="host-payout-net"
            className="mt-1 text-[30px] font-extrabold whitespace-nowrap text-ink tabular-nums"
          >
            {signed(t.net_minor)}
          </div>
        </div>
        <span
          data-testid="host-payout-status"
          className={`${PILL} ${paid ? 'bg-semantic-success-bg text-semantic-success-text' : 'bg-semantic-warning-bg text-semantic-warning-text'}`}
        >
          {paid ? `Paid${data.payment?.at ? ` ${paidDay(data.payment.at)}` : ''}` : 'Not paid yet'}
        </span>
      </div>
      {data.incomplete.length > 0 ? (
        <p
          data-testid="host-payout-incomplete"
          className="mt-3 rounded-lg bg-semantic-warning-bg px-3 py-2.5 text-sm text-semantic-warning-text"
        >
          <b className="text-charcoal">Payout incomplete.</b> CARii has not set{' '}
          {data.incomplete
            .map(
              (c) =>
                `the ${c.missing.map((m) => MISSING[m]).join(' and ')} for ${c.plate_number ?? c.label}`,
            )
            .join('; ')}
          . Ask CARii; the figure will change.
        </p>
      ) : null}
      <div data-testid="host-payout-lines" className="mt-3 border-t border-hairline-soft">
        <Line
          label={
            <>
              Rental revenue{' '}
              <span className="text-xs text-steel">
                ({data.rentals} {data.rentals === 1 ? 'rental' : 'rentals'})
              </span>
            </>
          }
          value={money(t.revenue_minor)}
        />
        <Line label="Customer penalties" value={`+ ${money(t.penalty_minor)}`} />
        <Line label="Compensation" value={`− ${money(t.compensation_minor)}`} />
        <Line label="Self bookings" value={`− ${money(t.self_booking_minor)}`} />
        <Line label="Supplier cost" value={`− ${money(t.supplier_total_cost_minor)}`} />
        <Line label="CARii commission" value={`− ${money(t.commission_minor)}`} />
        <div className="mt-0.5 flex justify-between gap-3 border-t-2 border-ink pt-2 text-sm font-extrabold text-ink tabular-nums">
          <span>Net payout</span>
          <span>{signed(t.net_minor)}</span>
        </div>
      </div>
      <p data-testid="host-money-note" className="mt-3 text-xs text-steel">
        Whole month, bookings paid ahead included. Platform fee {money(t.platform_fee_minor)} is
        CARii's and not in your revenue.
      </p>
    </Card>
  );
}

function Penalties({ data }: { data: HostMoneyData }) {
  const p = data.penalties;
  return (
    <Card data-testid="host-penalties" className="min-w-0">
      <div className="flex items-baseline justify-between gap-3">
        <div className={LABEL}>Customer penalties</div>
        <span className="text-sm whitespace-nowrap text-steel">{p.count} this month</span>
      </div>
      <div className="mt-3 grid grid-cols-3 gap-px overflow-hidden rounded-lg border border-hairline bg-hairline-soft">
        {(
          [
            ['total', 'Total', p.total_minor, 'text-ink'],
            ['paid', 'Paid by customer', p.paid_minor, 'text-ink'],
            ['owed', 'Still owed', p.owed_minor, 'text-semantic-danger-text'],
          ] as const
        ).map(([key, label, value, tone]) => (
          <div key={key} className="min-w-0 bg-canvas p-3 dark:bg-surface">
            <div className={LABEL}>{label}</div>
            <div
              data-testid={`host-penalties-${key}`}
              className={`mt-0.5 text-sm font-bold whitespace-nowrap tabular-nums sm:text-lg ${tone}`}
            >
              {money(value)}
            </div>
          </div>
        ))}
      </div>
      {p.latest.length === 0 && p.other_minor === 0 ? (
        <p className="mt-3 text-sm text-steel">None this month.</p>
      ) : (
        <div className="mt-2">
          {p.latest.map((r) => (
            <div
              key={r.id}
              data-testid="host-penalty-row"
              className="flex items-center justify-between gap-3 border-b border-hairline-soft py-2 text-sm"
            >
              <span className="min-w-0 text-slate">
                {penaltyDay(r.on)} · {r.plate_number} ·{' '}
                {PENALTY_TYPE_LABEL[r.type as PenaltyType] ?? r.type}
              </span>
              <span className="flex shrink-0 items-center gap-2 tabular-nums">
                <span
                  className={`${PILL} ${r.paid_on ? 'bg-semantic-success-bg text-semantic-success-text' : 'bg-semantic-danger-bg text-charcoal ring-1 ring-semantic-danger-text/45'}`}
                >
                  {r.paid_on ? 'Paid' : 'Owed'}
                </span>
                <span className="text-ink">{money(r.amount_minor)}</span>
              </span>
            </div>
          ))}
          {p.other_minor > 0 ? (
            <div
              data-testid="host-penalties-other"
              className="flex justify-between gap-3 py-2 text-sm"
            >
              <span className="text-slate">Other statement lines</span>
              <span className="text-ink tabular-nums">{money(p.other_minor)}</span>
            </div>
          ) : null}
        </div>
      )}
    </Card>
  );
}

function Cars({ data }: { data: HostMoneyData }) {
  const [open, setOpen] = useState<string | null>(null);
  const top = Math.max(...data.cars.map((c) => c.revenue_minor), 0);
  return (
    <Card padding="none" data-testid="host-cars" className="px-4 py-1.5">
      <div
        aria-hidden="true"
        className={`hidden border-b border-hairline py-2 sm:grid sm:grid-cols-[minmax(0,1.4fr)_64px_56px_minmax(0,1fr)_110px] sm:gap-x-4 ${LABEL}`}
      >
        <span>Car</span>
        <span className="text-right">Rentals</span>
        <span className="text-right">Days</span>
        <span />
        <span className="text-right">Revenue</span>
      </div>
      {data.cars.map((c) => (
        <Fragment key={c.car_id}>
          <div
            data-testid="host-car-row"
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1 border-b border-hairline-soft py-2.5 text-sm sm:grid-cols-[minmax(0,1.4fr)_64px_56px_minmax(0,1fr)_110px]"
          >
            <button
              type="button"
              aria-expanded={open === c.car_id}
              onClick={() => setOpen(open === c.car_id ? null : c.car_id)}
              className="flex min-w-0 items-center gap-1.5 text-left"
              disabled={c.bookings.length === 0}
            >
              {c.bookings.length > 0 ? (
                open === c.car_id ? (
                  <ChevronDown className="h-3.5 w-3.5 shrink-0 text-steel" aria-hidden="true" />
                ) : (
                  <ChevronRight className="h-3.5 w-3.5 shrink-0 text-steel" aria-hidden="true" />
                )
              ) : (
                <span className="w-3.5 shrink-0" />
              )}
              <span className="min-w-0">
                <b className="block truncate font-semibold text-ink">{c.label}</b>
                <span className="block text-xs text-steel">
                  {c.plate_number ?? '—'}
                  <span className="sm:hidden">
                    {' '}
                    · {c.rentals} {c.rentals === 1 ? 'rental' : 'rentals'} · {c.rental_days} days
                  </span>
                </span>
              </span>
            </button>
            <span className="hidden text-right text-ink tabular-nums sm:block">{c.rentals}</span>
            <span className="hidden text-right text-ink tabular-nums sm:block">
              {c.rental_days}
            </span>
            <span className="hidden sm:block" aria-hidden="true">
              <span className="block h-1.5 rounded-full bg-brand-blue-soft dark:bg-hairline-strong">
                <span
                  className="block h-full rounded-full bg-primary"
                  style={{ width: `${top > 0 ? (c.revenue_minor / top) * 100 : 0}%` }}
                />
              </span>
            </span>
            <b
              className={`text-right font-semibold whitespace-nowrap tabular-nums ${c.revenue_minor > 0 ? 'text-ink' : 'text-steel'}`}
            >
              {money(c.revenue_minor)}
            </b>
          </div>
          {open === c.car_id
            ? c.bookings.map((b) => (
                <div
                  key={b.id}
                  data-testid="host-car-booking"
                  className="flex justify-between gap-3 border-b border-hairline-soft bg-surface-soft py-2 pr-1 pl-7 text-sm text-slate dark:bg-surface"
                >
                  <span>
                    {dayOf(b.pickup_at)} – {dayOf(b.return_at)}
                  </span>
                  <span className="tabular-nums text-ink">{money(b.amount_minor)}</span>
                </div>
              ))
            : null}
        </Fragment>
      ))}
    </Card>
  );
}

export function HostMoney({ currentMonth }: { currentMonth: string }) {
  const [month, setMonth] = useState(currentMonth);
  const read = useQuery({
    queryKey: ['dashboard', 'money', month],
    queryFn: () => api.get<HostMoneyData>(`/dashboard/money?month=${month}`),
    retry: false,
  });
  const data = read.data;
  const atCurrent = month >= currentMonth;

  return (
    <section aria-label="Your money" data-testid="host-money" className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="text-[13px] font-bold tracking-wider text-ink uppercase">
          Your money · <span data-testid="host-money-month">{monthName(month)}</span>
        </h2>
        <div className="flex items-center gap-3">
          <div className="inline-flex overflow-hidden rounded-lg border border-hairline-strong">
            <button
              type="button"
              aria-label="Previous month"
              onClick={() => setMonth(shiftMonth(month, -1))}
              className="px-2.5 py-1.5 text-charcoal hover:bg-surface"
            >
              <ChevronLeft className="h-4 w-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              aria-label="Next month"
              disabled={atCurrent}
              onClick={() => setMonth(shiftMonth(month, 1))}
              className="border-l border-hairline-strong px-2.5 py-1.5 text-charcoal hover:bg-surface disabled:text-muted disabled:hover:bg-transparent"
            >
              <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </button>
          </div>
          {data ? (
            <Link
              to={`/sales-statement?from=${data.from}&to=${data.to}`}
              className="text-sm font-medium text-primary hover:underline dark:text-brand-blue-mid"
            >
              See full statement →
            </Link>
          ) : null}
        </div>
      </div>
      {data ? (
        <>
          <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-4 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
            <Payout data={data} />
            <Penalties data={data} />
          </div>
          <h3 className="text-[13px] font-bold tracking-wider text-slate uppercase">
            Rental fees by car
          </h3>
          <Cars data={data} />
        </>
      ) : read.isError ? (
        <Card data-testid="host-money-error">
          <p className="text-sm text-steel">Your money could not be loaded. Try again shortly.</p>
          <button
            type="button"
            onClick={() => read.refetch()}
            className="mt-3 rounded-lg border border-hairline-strong px-3 py-1.5 text-sm font-medium text-charcoal hover:bg-surface"
          >
            Try again
          </button>
        </Card>
      ) : (
        <div className="flex justify-center py-10">
          <Spinner size="lg" />
        </div>
      )}
    </section>
  );
}
